import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getQueryResponse,
  clearQuery,
  startStreamingQuery,
  appendStreamingChunk,
  finishStreamingQuery,
} from '../features/bills/billSlice';
import { BounceLoader as Spinner } from 'react-spinners';
import PrintQueryLog from './QueryFuncs/PrintQueryLog';
import recommendedQueries from './QueryFuncs/RecommendedQueries';

const BillQueryForm = ({ billText, version, billId, keyChanges, session, stateCode, state, isQueryDisabled }) => {
  const { queries, isLoadingQuery: isLoading } = useSelector((state) => state.bill);
  const user = useSelector((state) => state.user);
  const auth = user.searchTerm;
  const [query, setQuery] = useState('');
  const [notification, setNotification] = useState('');
  const dispatch = useDispatch();

  const submitQuery = async () => {
    if (notification.length > 0) setNotification('');

    if (query.trim().toLowerCase() === 'clear') {
      dispatch(clearQuery({ auth }));
      setQuery('');
      return;
    }

    if (!query.trim()) {
      setNotification('Please enter a question.');
      return;
    }

    const textToSend = billText.length > 50000 ? keyChanges : billText;
    const urlState = (typeof window !== 'undefined' && window.location && window.location.pathname.split('/')[1]) || '';
    const statePrefix = (stateCode || (typeof state !== 'undefined' ? state : '') || urlState || '').toLowerCase();
    const endpoint = `/api/${statePrefix}/bills/query`;
    const body = { auth, query, billText: textToSend, billId, session, mode: 'precision', version };

    dispatch(startStreamingQuery({ query }));
    try {
      const storedUser = JSON.parse(localStorage.getItem('user'));
      const headers = { 'Content-Type': 'application/json', 'Cache-Control': 'no-cache' };
      if (storedUser?.token) headers['Authorization'] = storedUser.token;

      const response = await fetch(endpoint, { method: 'POST', headers, body: JSON.stringify(body) });
      if (!response.ok) throw new Error(`HTTP ${response.status}: ${await response.text()}`);

      // The server streams plain text when streaming is enabled, otherwise returns the answer as JSON.
      if ((response.headers.get('content-type') || '').includes('application/json') || !response.body) {
        dispatch(appendStreamingChunk({ chunk: await response.json() }));
      } else {
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        try {
          let done = false;
          while (!done) {
            const { value, done: readerDone } = await reader.read();
            done = readerDone;
            if (value) {
              const chunk = decoder.decode(value, { stream: true });
              if (chunk) dispatch(appendStreamingChunk({ chunk }));
            }
          }
        } finally {
          reader.releaseLock();
        }
      }
      dispatch(finishStreamingQuery());
    } catch (err) {
      console.error('Query request failed, retrying without streaming:', err);
      dispatch(finishStreamingQuery());
      try {
        await dispatch(getQueryResponse(body)).unwrap();
      } catch (fallbackErr) {
        console.error('Fallback request also failed:', fallbackErr);
        setNotification(`Error: ${fallbackErr.message || fallbackErr}`);
      }
    }
  };

  const handleSubmit = (e) => {
    // Prevent any implicit form submits; use explicit buttons only
    e.preventDefault();
    return false;
  };

  const handleSelectRecommendedQuery = (queryText) => {
    setQuery(queryText);
  };

  // Function to process and return text with bolded words and numbered list
  const renderResponseWithBold = (response) => {
    // First, split the response into parts based on the pattern **bold** and numbers followed by periods
    const parts = response.split(/(\*\*[^*]+\*\*)|(\d+\.\s)/g); // This regex captures both patterns

    return parts.map((part, index) => {
      if (!part) return null; // Filter out any null or undefined parts that may result from split

      // Check for bold pattern
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>; // Remove ** and wrap in <strong>
      }

      // Check for number followed by a period, indicating a new list item
      if (/\d+\.\s/.test(part)) {
        return (
          <React.Fragment key={index}>
            <br />
            <br />
            {part} {/* Add a line break before the number */}
          </React.Fragment>
        );
      }

      // Return other text as is
      return part;
    });
  };

  return (
    <div className="mb-[20px]">
      <div className="rounded-lg border border-border bg-card px-5 py-4 shadow-sm">
        <form onSubmit={handleSubmit}>
          {/* Conditional rendering of label text based on isQueryDisabled */}
          <div className="mb-5 border-b border-border pb-2.5">
            <div className="flex items-center justify-between">
              <h2 id="Office-Information" className="font-serif text-2xl font-semibold leading-8 text-foreground">
                {isQueryDisabled ? 'Query Disabled for HB1' : 'Enter your query about the bill:'}
              </h2>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 mb-[30px]">
            {recommendedQueries['precision'].map((rq, index) => (
              <button
                type="button"
                className="w-full max-w-[186px] whitespace-nowrap rounded-md border border-border bg-card px-1.5 py-2.5 text-center text-sm font-medium text-info transition-colors hover:bg-muted"
                key={index}
                onClick={() => handleSelectRecommendedQuery(rq.query)}
              >
                {rq.label}
              </button>
            ))}
          </div>

          <div className="flex gap-4 mb-[20px]">
            <input
              type="text"
              className="h-10 flex-1 rounded-md border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={isQueryDisabled ? '' : 'Type your query here...'} // Optionally clear the placeholder if disabled
              disabled={isQueryDisabled}
            />
            <button
              type="button"
              className="inline-flex h-10 items-center whitespace-nowrap rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
              disabled={isLoading || isQueryDisabled}
              onClick={submitQuery}
            >
              Submit Query
            </button>
          </div>
        </form>

        {isQueryDisabled && <p className="queryDisabledMessage">Query functionality is disabled for this bill.</p>}

        <div className="w-full rounded-lg border border-border bg-card p-5" id="queryLog">
          <div className="mb-5 flex items-center justify-between">
            <h2 id="Office-Information" className="font-serif text-2xl font-semibold leading-8 text-foreground">
              Query Log:
            </h2>
            <div className="flex items-center gap-[15px]">
              <button
                className="inline-flex h-10 items-center rounded-md border border-border bg-card px-6 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
                onClick={PrintQueryLog}
                disabled={queries.length === 0}
              >
                Print
              </button>
            </div>
          </div>
          {isLoading ? (
            <div className="w-full h-full flex justify-center items-center">
              <Spinner loadingContext={'query'} color="hsl(211 31% 25%)" size={150} />
            </div>
          ) : (
            <div className="">
              {queries.length === 0 ? (
                <>
                  <p className="noQuery">No queries submitted yet.</p>
                  <div className="notification">{notification}</div>
                </>
              ) : (
                queries &&
                queries
                  .slice()
                  .reverse()
                  .map((entry, index) => (
                    <div className="flex items-start gap-4 mb-[20px]" key={index}>
                      <div className="flex flex-col gap-[25px]">
                        <div className="flex flex-wrap items-center gap-[10px] md:gap-[15px]">
                          <h6 className="font-serif text-xl font-semibold leading-7 text-foreground">Query: </h6>
                          <p className="text-base leading-7 text-foreground">{entry.query}</p>
                        </div>
                        <div className="flex items-start gap-[15px]">
                          <h6 className="font-serif text-xl font-semibold leading-7 text-foreground">
                            Response:{' '}
                          </h6>
                          <p className="text-base leading-7 text-foreground">
                            {renderResponseWithBold(entry.response)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillQueryForm;
